/**
 * Панель поиска и выбора клиента/сделки
 */

import React, { useState } from 'react';
import { Formik, Form } from 'formik';
import { TextInput, Select } from '../../../components/UI/FormItems';
import type { ClientSearchForm, AdminClient, Deal, ClientData } from '../../../types';
import { fetchClients, fetchDeals, fetchClientData } from '../../../api/AdminApi';
import { LoadingSpinner } from './LoadingSpinner';
import styles from './ClientSearchPanel.module.css';

interface ClientSearchPanelProps {
  clientId: number | null;
  selectedClientName: string;
  creditContractId: number | null;
  onClientSelect: (clientData: ClientData, deals: Deal[]) => void;
  onDealSelect: (dealId: number | null) => void;
}

/**
 * Компонент панели поиска клиента
 */
export const ClientSearchPanel: React.FC<ClientSearchPanelProps> = ({
  clientId,
  selectedClientName,
  creditContractId,
  onClientSelect,
  onDealSelect,
}) => {
  const [clients, setClients] = useState<AdminClient[]>([]);
  const [deals, setDeals] = useState<Deal[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isLoadingClient, setIsLoadingClient] = useState(false);


  const initialValues: ClientSearchForm = {
    lastname: '',
    firstname: '',
    middlename: '',
    series: '',
    number: '',
  };

  const handleSearch = async (values: ClientSearchForm) => {
    setIsSearching(true);
    try {
      const searchParams = {
        lastname: values.lastname || undefined,
        firstname: values.firstname || undefined,
        middlename: values.middlename || undefined,
        series: values.series || undefined,
        number: values.number || undefined,
      };

      const foundClients = await fetchClients(searchParams);
      setClients(foundClients);
    } finally {
      setIsSearching(false);
    }
  };

  const handleClientChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const clientIdValue = parseInt(e.target.value, 10);
    if (!clientIdValue) return;

    setIsLoadingClient(true);

    try {
      // Получаем сделки клиента
      const clientDeals = await fetchDeals(clientIdValue);
      setDeals(clientDeals);

      // Получаем полные данные клиента напрямую с актуальным ID
      const clientDataResponse = await fetchClientData(clientIdValue);
      if (clientDataResponse) {
        onClientSelect(clientDataResponse, clientDeals);
      }
    } finally {
      setIsLoadingClient(false);
    }
  };

  const handleDealChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const dealId = parseInt(e.target.value, 10);
    onDealSelect(dealId || null);
  };

  return (
    <div className={styles.searchPanel}>
      {(isSearching || isLoadingClient) && (
        <div className={styles.loadingOverlay}>
          <LoadingSpinner
            size="medium"
            text={isSearching ? 'Поиск клиентов...' : 'Загрузка данных...'}
          />
        </div>
      )}

      <Formik initialValues={initialValues} onSubmit={handleSearch}>
        {({ values }) => (
          <Form className={styles.formContainer}>
            {/* Поля поиска с улучшенным дизайном */}
            <div className={styles.searchSection}>
              <div className={styles.sectionTitle}>📋 Поиск по ФИО</div>
              <div className={styles.fieldsColumn}>
                <div className={styles.fieldWrapper}>
                  <TextInput
                    label=""
                    name="lastname"
                    placeholder="Фамилия"
                  />
                </div>
                <div className={styles.fieldWrapper}>
                  <TextInput
                    label=""
                    name="firstname"
                    placeholder="Имя"
                  />
                </div>
                <div className={styles.fieldWrapper}>
                  <TextInput
                    label=""
                    name="middlename"
                    placeholder="Отчество"
                  />
                </div>
              </div>
            </div>

            {/* Поля паспорта */}
            <div className={styles.searchSection}>
              <div className={styles.sectionTitle}>🪪 Поиск по паспорту</div>
              <div className={styles.passportContainer}>
                <div className={styles.passportField}>
                  <span className={styles.passportLabel}>Серия</span>
                  <TextInput
                    label=""
                    name="series"
                    placeholder="0000"
                    maxLength={4}
                  />
                </div>
                <div className={styles.passportField}>
                  <span className={styles.passportLabel}>Номер</span>
                  <TextInput
                    label=""
                    name="number"
                    placeholder="000000"
                    maxLength={6}
                  />
                </div>
              </div>
            </div>

            {/* Кнопка поиска */}
            <button
              className={`btn ${styles.searchButton}`}
              type="submit"
              disabled={isSearching}
            >
              {isSearching ? '⏳ Поиск...' : '🔍 Найти клиента'}
            </button>

            {/* Результаты поиска */}
            {clients.length > 0 && (
              <div className={styles.resultsSection}>
                <div className={styles.resultsCount}>
                  👥 Найдено клиентов: {clients.length}
                </div>
                <div className={styles.selectWrapper}>
                  <Select
                    label=""
                    name="client"
                    onChange={handleClientChange}
                    disabled={isLoadingClient}
                  >
                    <option value={0}>Выберите клиента</option>
                    {clients.map((client) => (
                      <option key={client.id} value={client.id}>
                        {client.fullName}
                      </option>
                    ))}
                  </Select>
                </div>
              </div>
            )}

            {/* Сделки клиента */}
            {deals.length > 0 && (
              <div className={styles.resultsSection}>
                <div className={styles.resultsCount}>
                  📄 Сделки клиента: {deals.length}
                </div>
                <div className={styles.selectWrapper}>
                  <Select
                    label=""
                    name="deal"
                    onChange={handleDealChange}
                  >
                    <option value={0}>Выберите сделку</option>
                    {deals.map((deal) => (
                      <option key={deal.id} value={deal.id}>
                        {deal.number}
                      </option>
                    ))}
                  </Select>
                </div>
              </div>
            )}
          </Form>
        )}
      </Formik>
    </div>
  );
};
